import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog'
import DataSource from 'devextreme/data/data_source'
import { TaskMaster } from 'src/app/types/dto/tasks.dto'
import { Subject, of, zip } from 'rxjs'
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators'
import { ConfirmService } from 'src/app/shared/components/confirm/confirm.service'
import { CardApiService } from 'src/app/shared/services/api/card.api.service'
import { TasksApiService } from 'src/app/shared/services/api/tasks.api.service'
import { DateUtilsService } from 'src/app/shared/services/date-utils.service'
import { LogService } from 'src/app/shared/services/log.service'
import { NotificationService } from 'src/app/shared/services/notification.service'
import { UtilsService } from 'src/app/shared/services/utils.service'
import { ScreenService } from 'src/app/shared/services/screen.service'
import { ChartField } from 'src/app/types/chart.model'
import { DEFAULT_RANGE, DEFAULT_VALUE_FIELD } from 'src/app/types/constants'
import { TRACKING_VALUE_FIELD_SENTINEL } from 'src/app/types/datasources/forecast-tasks.model'
import { AppMatCard } from 'src/app/types/dashboard.model'
import { TrackingFieldDto } from 'src/app/types/dto/tracking-field.dto'
import { PatchDashCardRequest } from 'src/app/types/request/dashboard.req'
import {
  CardSize,
  CardType,
  ChartDataSource,
  ChartType,
  DateAxisType,
  DateRangeType,
  MetricDisplayType,
  MetricSplitSubsetMode,
  TargetComparison,
  TaskDurationAggregation,
  ValueAggregationType,
} from 'src/app/types/enums'
import { MAX_DASHBOARD_NAME_LEN } from 'src/config/global-config'
import { DashboardService } from '../dashboard/dashboard.service'
import { DatasourceConfigService } from 'src/app/shared/services/chart-datasource/datasource-config.service'
import { ChartDatasourceProviderService } from 'src/app/shared/services/chart-datasource/chart-datasource-provider.service'
import { TrackingFieldsApiService } from 'src/app/shared/services/api/tracking-fields.api.service'
import { JobLookupApiService } from 'src/app/shared/services/api/job-lookup.api.service'
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatHint, MatSelect, MatOptgroup, MatOption, MatSuffix } from '@angular/material/select';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTooltipModule } from 'devextreme-angular/ui/tooltip';
import { DxColorBoxModule } from 'devextreme-angular/ui/color-box';
import { DxFilterBuilderModule } from 'devextreme-angular/ui/filter-builder';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { MatButton } from '@angular/material/button';
import { DatasourceNamePipe } from '../../../shared/data-source-name.pipe';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

type TaskSelectionMode = 'taskType' | 'taskCode'
export type ChartFieldGroup = { label: string; fields: ChartField[] }

export const FIELD_GROUPING_THRESHOLD = 15
const FIELD_TYPE_LABELS: Record<ChartField['dataType'], string> = {
  string: 'Text',
  boolean: 'Yes / No',
  number: 'Number',
  date: 'Date',
}

export const JOB_FIELD_GROUP_LABEL = 'Job (current state of associated Job)'
export const JOB_ROLE_GROUP_LABEL = 'Job Roles'

export function groupChartFieldsByType(options: ChartField[]): ChartFieldGroup[] {
  const jobFields = options.filter((option) => option.fieldGroup === 'job')
  if (options.length <= FIELD_GROUPING_THRESHOLD && !jobFields.length) return []

  const dataTypeGroups = (Object.keys(FIELD_TYPE_LABELS) as ChartField['dataType'][])
    .map((dataType) => ({
      label: FIELD_TYPE_LABELS[dataType],
      fields: options.filter((option) => option.dataType === dataType && !option.fieldGroup),
    }))
    .filter((group) => group.fields.length > 0)

  const jobRoles = options.filter((option) => option.fieldGroup === 'jobRole')
  const jobGroups = [
    ...(jobFields.length ? [{ label: JOB_FIELD_GROUP_LABEL, fields: jobFields }] : []),
    ...(jobRoles.length ? [{ label: JOB_ROLE_GROUP_LABEL, fields: jobRoles }] : []),
  ]

  // non-job datasources list their own fields first; the Jobs datasource keeps roles at the top
  return jobFields.length ? [...dataTypeGroups, ...jobGroups] : [...jobGroups, ...dataTypeGroups]
}

/** field names used by conditions in a DevExtreme filter expression */
export function getFilterFieldNames(filter: unknown): string[] {
  if (!Array.isArray(filter)) return []
  if (typeof filter[0] === 'string' && typeof filter[1] === 'string' && filter[0] !== '!') {
    return [filter[0]]
  }
  return filter.flatMap((part) => getFilterFieldNames(part))
}

export const DISABLED_SEPARATE_SERIES = 'DO NOT GROUP'
export const DISABLED_SEPARATE_SERIES_OPTION: ChartField = {
  displayName: DISABLED_SEPARATE_SERIES,
  dataField: DISABLED_SEPARATE_SERIES,
  dataType: 'boolean',
}

@Component({
    selector: 'app-edit-card',
    templateUrl: './edit-card.component.html',
    styleUrls: ['./edit-card.component.scss'],
  imports: [MatDialogTitle, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, MatFormField, NgClass, MatLabel, MatInput, MatHint, MatSelect, MatOptgroup, MatOption, DxSelectBoxModule, SpinnerComponent, MatSuffix, MatCheckbox, MatIcon, DxTooltipModule, MatTooltip, MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker, DxColorBoxModule, MatButton, DxFilterBuilderModule, MatDialogActions, MatDialogClose, DatasourceNamePipe, NgxMatSelectSearchModule, MatButtonToggleModule]
})
export class EditCardComponent implements OnInit, OnDestroy {
  private UntypedFormBuilder = inject(UntypedFormBuilder);
  private dashboardService = inject(DashboardService);
  private datasourceConfigService = inject(DatasourceConfigService);
  private cardApi = inject(CardApiService);
  private notiService = inject(NotificationService);
  private tasksApi = inject(TasksApiService);
  private chartDataService = inject(ChartDatasourceProviderService);
  private dateUtils = inject(DateUtilsService);
  private utils = inject(UtilsService);
  private logger = inject(LogService);
  private confirmService = inject(ConfirmService);
  private trackingFieldsApi = inject(TrackingFieldsApiService);
  private jobLookupApi = inject(JobLookupApiService);
  private dialogRef = inject<MatDialogRef<EditCardComponent>>(MatDialogRef);
  screen = inject(ScreenService);

  MAX_NAME_LEN = MAX_DASHBOARD_NAME_LEN
  // titles longer than this can overflow on quadrant (extraSmall) cards - warn, don't block
  TITLE_OVERFLOW_WARN_LEN = 19
  MAX_DESC_LENGTH = 150

  card?: AppMatCard
  form?: UntypedFormGroup
  /** metrics share most of the chart edit form, but omit series/x-axis/chart-type fields */
  isMetric = false

  readonly CardType = CardType
  readonly DISABLED_SEPARATE_SERIES_OPTION = DISABLED_SEPARATE_SERIES_OPTION

  get isExtraSmallCard(): boolean {
    return this.card?.size === CardSize.extraSmall
  }

  get willReduceCardSize(): boolean {
    return (
      this.card?.cardType === CardType.Chart &&
      this.isMetric &&
      (this.card.size === CardSize.medium || this.card.size === CardSize.large)
    )
  }

  get metricTypeDisabled(): boolean {
    const source = this.form?.controls.datasource.value as string | undefined
    if (!source) return false

    const datasource = ChartDataSource[source as keyof typeof ChartDataSource]
    return !this.datasourceConfigService.getMetricSupportedDatasources().includes(datasource)
  }

  get isForecastTasksInProgress(): boolean {
    const source = this.form?.controls.datasource.value as string | undefined
    return ChartDataSource[source as keyof typeof ChartDataSource]
      === ChartDataSource.ForecastedTasksInProgress
  }

  get isJobsBetweenTasks(): boolean {
    const source = this.form?.controls.datasource.value as string | undefined
    return ChartDataSource[source as keyof typeof ChartDataSource]
      === ChartDataSource.JobsBetweenTasks
  }

  get firstTaskLabel(): string {
    return this.isJobsBetweenTasks ? 'Completed Task' : this.showTask2Fields ? 'Task 1 Type' : 'Task Type'
  }

  get secondTaskLabel(): string {
    return this.isJobsBetweenTasks ? 'Not Completed Task' : 'Task 2 Type (optional)'
  }

  taskTypes: DataSource | undefined
  taskTypeGroups: { groupName: string; tasks: TaskMaster[] }[] = []
  taskCodes: string[] = []
  taskTypeSearchCtrl = new FormControl('')
  taskCodeSearchCtrl = new FormControl('')

  get canSplitMetric(): boolean {
    return this.isMetric &&
      this.form?.controls.metricDisplayType.value === MetricDisplayType.Gauge &&
      !this.isExtraSmallCard
  }

  get chartColourDisabled(): boolean {
    return this.isMetric &&
      this.form?.controls.metricDisplayType.value === MetricDisplayType.Gauge &&
      this.form?.controls.targetValue.value != null
  }

  get hasMetricSplit(): boolean {
    return this.canSplitMetric &&
      this.form?.controls.splitByField.value?.dataField !== DISABLED_SEPARATE_SERIES
  }

  chartOptions = Object.keys(ChartType).filter((k) => isNaN(Number(k)))
  groupOptions = Object.keys(DateAxisType).filter((k) => isNaN(Number(k)))
  taskDurationOptions = Object.keys(TaskDurationAggregation).filter((k) => isNaN(Number(k)))
  readonly targetComparisonOptions = [
    { value: TargetComparison.AtLeast, label: 'At least' },
    { value: TargetComparison.AtMost, label: 'At most' },
  ]
  readonly metricSplitSubsetOptions = [
    { value: MetricSplitSubsetMode.Highest, label: 'Highest' },
    { value: MetricSplitSubsetMode.Lowest, label: 'Lowest' },
    { value: MetricSplitSubsetMode.ClosestToTarget, label: 'Closest to target', requiresTarget: true },
    { value: MetricSplitSubsetMode.FurthestFromTarget, label: 'Furthest from target', requiresTarget: true },
    { value: MetricSplitSubsetMode.HighestUnderTarget, label: 'Highest under target', requiresTarget: true },
    { value: MetricSplitSubsetMode.LowestOverTarget, label: 'Lowest over target', requiresTarget: true },
  ]
  readonly metricSplitDisplayCountOptions = [1, 2, 3, 4, 5, 6, 7, 8]
  seriesOptions: ChartField[] = []
  seriesOptionGroups: ChartFieldGroup[] = []
  rangeOptions?: string[]
  dataSourceFilterFields?: any[]
  argFieldOptions: ChartField[] = []
  argFieldOptionGroups: ChartFieldGroup[] = []
  argFieldIsDateType = false
  valueFieldOptions: ChartField[] = []
  showValueAggregation = false
  dateFilterFieldOptions: ChartField[] = []
  showDateFilterField = true
  filter: any
  chartColour: string

  get selectedDateFilterFieldName(): string {
    const selectedField = this.form?.controls.dateFilterField.value
    return this.dateFilterFieldOptions.find((field) => field.dataField === selectedField)?.displayName ?? 'date'
  }

  get unavailableFieldsInUse(): string[] {
    if (!this.form || !this.unavailableFieldLabels.size) return []

    const inUse = new Set<string>([
      ...(this.isMetric ? [] : [this.form.controls.argumentField.value]),
      this.form.controls.splitByField.value?.dataField,
      ...getFilterFieldNames(this.filter),
    ])
    return Array.from(this.unavailableFieldLabels)
      .filter(([dataField]) => inUse.has(dataField))
      .map(([, label]) => label)
  }

  rangeTypeEnum = DateRangeType

  get saveDisabled(): boolean {
    return !this.form || this.form.invalid || this.loading || this.unavailableFieldsInUse.length > 0
  }

  loading = true
  tasksError = false
  jobFieldsError = false
  /** saved fields this card uses that are no longer offered, keyed by field name */
  unavailableFieldLabels = new Map<string, string>()
  rangeNotAlignToGrouping = false
  isPresetDateRange = false
  cumulativeAggregation = false
  usingRollingPeriods = false
  isTaskSource = false
  showTaskSelectionMode = false

  showTask1DateField = false
  showTask2Fields = false
  showValueField = true
  showArgumentFIeld = true
  showDurationAggregation = false
  showShowFilteredCategoriesOnly = false
  showGroupBySeries = true // currently all datasources support this
  hideStartingValue = false
  hideChartColorSelection = false
  showExcludeInactive = false

  // Tracking field for Forecast Tasks datasource
  showTrackingField = false
  trackingFieldOptions: TrackingFieldDto[] = []
  loadingTrackingFields = false

  // Custom target per-period feature
  showCustomTargets = false
  targetPeriods: { label: string; key: string; value: number | null }[] = []
  loadingTargetPeriods = false
  // changes that affect custom targets
  hasUnsavedSignificantChanges = false

  /** set when the card is a draft from Thomas that does not exist yet: Save adds it to this dashboard first */
  private draftDashboardId: number | undefined
  private startedAsDraft = false

  private readonly destroying = new Subject<null>()

  constructor() {
    const data = inject(MAT_DIALOG_DATA);

    this.card = data.card
    this.isMetric = this.card?.cardType === CardType.Metric
    this.chartColour = this.utils.getThemePrimaryColour()
    this.draftDashboardId = data.draftDashboardId
    this.startedAsDraft = this.isDraft
    // a Thomas change to the grouping, range, x-axis or filters invalidates saved per-period targets
    if (data.clearTargetOverrides) this.hasUnsavedSignificantChanges = true
  }

  get isDraft(): boolean {
    return !this.card?.id
  }

  /** metrics only support datasources whose fetch doesn't rely on grouping-heavy logic */
  get datasourceOptions(): string[] {
    if (this.isMetric) {
      return this.datasourceConfigService
        .getMetricSupportedDatasources()
        .map((ds) => ChartDataSource[ds])
        .sort()
    }

    return Object.keys(ChartDataSource)
      .filter((k) => isNaN(Number(k)))
      .sort()
  }

  /** rolling/cumulative aggregations are period-based and don't apply to a single metric value */
  get aggregationOptions(): string[] {
    const options = Object.keys(ValueAggregationType).filter((k) => isNaN(Number(k)))
    return (
      this.isMetric
        ? options.filter(
            (o) =>
              o === ValueAggregationType[ValueAggregationType.Sum] ||
              o === ValueAggregationType[ValueAggregationType.Average]
          )
        : options
    ).sort()
  }

  ngOnInit() {
    if (!this.card) {
      this.logger.log('edit-card', 'onInit', 'no card in edit-card')
      throw new Error('no card in edit-card')
    }

    if (this.card.filters) this.filter = JSON.parse(this.card.filters)

    if (this.card.chartColour) this.chartColour = this.card.chartColour

    if (this.card.taskDurationAggregation == undefined)
      this.card.taskDurationAggregation = TaskDurationAggregation.Week

    // roles can be refreshed while the dialog is open (lookup cache expiry, or a data load finding a new job)
    this.datasourceConfigService.jobRolesChanged$
      .pipe(takeUntil(this.destroying))
      .subscribe(() => this.refreshFieldOptions())
    this.initFormOnceJobFieldsLoaded()
    this.getTaskData()
  }

  /** job roles and placeholders must be in the field lists before the form takes its values from them */
  private initFormOnceJobFieldsLoaded(): void {
    this.jobLookupApi.getLookup()
      .pipe(takeUntil(this.destroying))
      .subscribe({
        next: () => {
          if (this.form) return
          this.initForm()

          // Auto-open custom targets section if there are existing overrides
          if (this.card?.config?.targetOverrides && Object.keys(this.card.config.targetOverrides).length > 0) {
            if (this.hasUnsavedSignificantChanges) {
              this.notiService.showInfo('Saving this change clears the custom targets set for each period.')
            } else {
              this.showCustomTargets = true
              this.generateTargetPeriods()
            }
          }
        },
        error: (err) => {
          this.jobFieldsError = true
          this.logger.log('edit-card', 'initFormOnceJobFieldsLoaded', err)
        },
      })
  }

  save() {
    this.loading = true
    this.form?.disable({ emitEvent: false })

    if (this.card && this.form) {
      const start: Date = this.form.controls.start.value
      const end: Date = this.form.controls.end.value
      const usingTaskCode = this.usesTaskCodeSelection()

      const model = {
        size:
          this.isMetric && this.card.size !== CardSize.small && this.card.size !== CardSize.extraSmall
            ? CardSize.small
            : undefined,
        title: this.form.controls.name.value,
        dataSourceId:
          ChartDataSource[this.form.controls.datasource.value as keyof typeof ChartDataSource],
        taskmasterId: usingTaskCode ? null : this.form.controls.taskType.value,
        taskmaster2Id: this.form.controls.taskType2.value,
        task1DateField: this.form.controls.task1DateField.value,
        task2DateField: this.form.controls.task2DateField.value,
        taskDurationAggregation:
          TaskDurationAggregation[
            this.form.controls.taskDurationOption.value as keyof typeof TaskDurationAggregation
          ],
        chartTypeId: ChartType[this.form.controls.chartType.value as keyof typeof ChartType],
        dateAxisTypeId:
          DateAxisType[this.form.controls.grouping.value as keyof typeof DateAxisType],
        valueField: this.form.controls.valueField.value,
        valueAggregationTypeId:
          ValueAggregationType[
            this.form.controls.valueAggregation.value as keyof typeof ValueAggregationType
          ],
        startingValue: this.form.controls.startingValue.value,
        argumentField: this.form.controls.argumentField.value,
        splitByField: this.form.controls.splitByField.value.dataField,
        showFilteredCategoriesOnly: this.form.controls.showFilteredCategoriesOnly.value,
        dateFilterField: this.form.controls.dateFilterField.value,
        dateRangeTypeId:
          DateRangeType[this.form.controls.rangeType.value as keyof typeof DateRangeType],
        dateFrom: this.isPresetDateRange
          ? undefined
          : start
          ? this.dateUtils.UTCStringWithoutTimeFromDate(start)
          : undefined,
        dateTo: this.isPresetDateRange
          ? undefined
          : end
          ? this.dateUtils.UTCStringWithoutTimeFromDate(end)
          : undefined,
        filters: this.filter ? JSON.stringify(this.filter) : '',
        chartColour: this.chartColour,
        targetValue: this.form.controls.targetValue.value,
        includeCurrentPartialPeriod: this.form.controls.includeCurrentPartialPeriod.value,
        additionalConfig: this.buildAdditionalConfig(),
      } as PatchDashCardRequest

      // a draft is added to the dashboard first; its id is kept so a failed settings save retries the same card
      const cardId$ = this.card.id
        ? of(this.card.id)
        : this.dashboardService.addDraftCard(this.card, this.form.controls.name.value, this.draftDashboardId!).pipe(tap((id) => (this.card!.id = id)))

      cardId$.pipe(switchMap((cardId) => this.cardApi.editCard(cardId, model))).subscribe({
        next: (updatedCard) => {
          this.dashboardService.updateCard(updatedCard)
          this.notiService.showSuccess(`${this.isMetric ? 'Metric' : 'Chart'} ${this.startedAsDraft ? 'Added' : 'Updated'}`)
          this.dialogRef.close()
        },
        error: (err) => {
          this.loading = false
          this.form?.enable()
          this.notiService.handleError(err)
          this.logger.log('edit-card', 'save', err)
        },
      })
    }
  }

  delete() {
    this.confirmService
      .confirmDelete(
        `Are you sure you want to DELETE the ${this.isMetric ? 'metric' : 'chart'} <b>${
          this.card?.title ?? 'ERROR'
        }</b>? <br><br> This is irreversible!`
      )
      .subscribe((confirm) => {
        if (!this.card) {
          this.logger.log('edit-card', 'delete', null, 'no card')
          throw new Error('no card to delete')
        }

        if (confirm)
          this.dashboardService
            .removeCard(this.card)
            .pipe(takeUntil(this.destroying))
            .subscribe({
              next: () => this.dialogRef.close(),
              error: (err) => {
                this.notiService.handleError(err)
                this.logger.log('edit-card', 'delete', err)
              },
            })
      })
  }

  ngOnDestroy(): void {
    this.destroying.next(null)
    this.destroying.complete()
  }

  get filteredTaskTypeGroups(): { groupName: string; tasks: TaskMaster[] }[] {
    const search = (this.taskTypeSearchCtrl.value ?? '').toLowerCase()
    if (!search) return this.taskTypeGroups
    return this.taskTypeGroups
      .map(g => ({ ...g, tasks: g.tasks.filter(t => t.taskTitle.toLowerCase().includes(search)) }))
      .filter(g => g.tasks.length > 0)
  }

  get filteredTaskCodes(): string[] {
    const search = (this.taskCodeSearchCtrl.value ?? '').toLowerCase()
    if (!search) return this.taskCodes
    return this.taskCodes.filter((taskCode) => taskCode.toLowerCase().includes(search))
  }

  private getTaskData() {
    zip(this.tasksApi.getTaskMastersForCurrentCompany(), this.tasksApi.getTaskCodes()).subscribe({
      next: ([res, taskCodes]) => {
        this.loading = false
        this.form?.enable()
        this.tasksError = false;
        this.taskCodes = taskCodes;
        const groupMap = new Map<string, TaskMaster[]>()
        res.forEach(t => {
          const g = groupMap.get(t.taskTypeDescription) ?? []
          g.push(t)
          groupMap.set(t.taskTypeDescription, g)
        })
        this.taskTypeGroups = Array.from(groupMap.entries())
          .map(([groupName, tasks]) => ({
            groupName,
            tasks: tasks.sort((a, b) => a.taskTitle.localeCompare(b.taskTitle)),
          }))
          .sort((a, b) => a.groupName.localeCompare(b.groupName))
      },
      error: (err) => {
        this.loading = false
        this.form?.enable()
        this.tasksError = true
        this.logger.log('edit-card', 'getJobMasters', err)
      },
    })
  }

  private fetchTrackingFields() {
    this.loadingTrackingFields = true
    this.trackingFieldsApi.getNumericTrackingFields().subscribe({
      next: (fields) => {
        this.trackingFieldOptions = fields
        this.loadingTrackingFields = false
      },
      error: (err) => {
        this.trackingFieldOptions = []
        this.loadingTrackingFields = false
        this.logger.log('edit-card', 'fetchTrackingFields', err)
      },
    })
  }

  private initForm() {
    if (this.card) {
      if (this.card.dataSourceId != null) this.resetDatasourceSpecificOptions(this.card.dataSourceId)

      this.form = this.UntypedFormBuilder.group(
        {
          cardType: [this.card.cardType],
          name: [
            this.card.title,
            [Validators.maxLength(this.MAX_NAME_LEN), Validators.minLength(2)],
          ],
          datasource: [
            this.card.dataSourceId != undefined
              ? ChartDataSource[this.card.dataSourceId]
              : undefined,
            [Validators.required],
          ],
          taskMode: [this.getInitialTaskSelectionMode()],
          taskType: [this.card.taskMasterId, [this.taskTypeRequired()]],
          taskCode: [this.card.config?.taskCode ?? null, [this.taskCodeRequired()]],
          task1DateField: [this.card.task1DateField, [this.taskDateFieldRequired()]],
          taskType2: [this.card.taskMaster2Id, [this.requiredIfSecondTaskDatasource()]],
          task2DateField: [this.card.task2DateField, [this.taskDateFieldRequired()]],
          chartType: [
            this.card.chartTypeId != undefined ? ChartType[this.card.chartTypeId] : undefined,
            this.isMetric ? [] : [Validators.required],
          ],
          valueField: [
            // 1st condition is for backwards compatibility with existing cards that used tracking field but didn't save it in config
            this.card.config?.trackingField && this.card.valueField === DEFAULT_VALUE_FIELD
              ? TRACKING_VALUE_FIELD_SENTINEL
              : this.card.valueField,
            [Validators.required]
          ],
          taskDurationOption: [
            this.card.taskDurationAggregation != undefined
              ? TaskDurationAggregation[this.card.taskDurationAggregation]
              : undefined,
            [this.requiredIfTaskDurationDatasource()],
          ],
          valueAggregation: [
            this.card.valueAggregationTypeId != undefined
              ? ValueAggregationType[this.card.valueAggregationTypeId]
              : undefined,
            [this.requiredIfCustomValueField()],
          ],
          splitByField: [
            this.card.splitByField
              ? this.getSeriesGroupingOption(this.card.splitByField)
              : DISABLED_SEPARATE_SERIES_OPTION,
          ],
          showFilteredCategoriesOnly: [this.card.showFilteredCategoriesOnly],
          startingValue: [this.card.startingValue ?? 0],
          argumentField: [this.card.argumentField, this.isMetric ? [] : [Validators.required]],
          dateFilterField: [this.card.dateFilterField, [this.dateFilterFieldRequired()]],
          rangeType: [
            this.card.dateRangeTypeId != undefined
              ? DateRangeType[this.card.dateRangeTypeId]
              : DEFAULT_RANGE,
            [Validators.required],
          ],
          start: [this.card.dateFrom ?? undefined, [this.requiredIfCustomRange()]],
          end: [this.card.dateTo ?? undefined, [this.requiredIfCustomRange()]],
          grouping: [
            this.card?.dateAxisTypeId != undefined
              ? DateAxisType[this.card.dateAxisTypeId]
              : undefined,
            [this.requiredForChart()],
          ],
          targetValue: [this.card.targetValue],
          targetComparison: [this.getInitialTargetComparison()],
          includeCurrentPartialPeriod: [this.card.includeCurrentPartialPeriod ?? false],
          includeRecordsWithNoDate: [this.card.config?.includeRecordsWithNoDate ?? false],
          showDescription: [this.card.config?.showDescription ?? !!this.card.config?.description],
          description: [this.card.config?.description ?? '', [Validators.maxLength(this.MAX_DESC_LENGTH)]],
          trackingField: [this.card.config?.trackingField ?? null],
          excludeInactiveUsers: [this.card.config?.excludeInactiveUsers ?? false],
          metricDisplayType: [this.card.metricDisplayType],
          metricSplitSubsetMode: [this.card.metricSplitSubsetMode],
          metricSplitDisplayCount: [this.card.metricSplitDisplayCount],
        },
        { validators: this.rollingPeriodAndRangeValidator() }
      )

      this.subscribeToFormChanges(this.form)
      if (this.form.controls.datasource.value) {
        this.datasourceChanged(this.form, this.form.controls.datasource.value)
      }
      this.updateExcludeInactiveVisibility()
      this.updateShowTrackingField(this.form.controls.valueField.value)
      this.showValueAggregation = this.valueFieldRequiresAggregation(this.form.controls.valueField.value)

      // conditional validators read this.form, which is unset while the group is being built
      Object.values(this.form.controls).forEach((control) => control.updateValueAndValidity({ emitEvent: false }))
    }
  }

  private getSeriesGroupingOption(groupByField: string): ChartField {
    return (
      this.seriesOptions.find((option) => option.dataField === groupByField) ??
      DISABLED_SEPARATE_SERIES_OPTION
    )
  }

  /** option lists are rebuilt with new objects, so select by field name rather than identity */
  compareFields = (a?: ChartField, b?: ChartField): boolean => a?.dataField === b?.dataField

  private getSeriesOptions(src: ChartDataSource) {
    this.seriesOptions = [DISABLED_SEPARATE_SERIES_OPTION].concat(
      this.datasourceConfigService.getDatasourceSeriesGroupFields(src)
    )
    this.groupSeriesOptions()
  }

  private groupSeriesOptions() {
    this.seriesOptionGroups = groupChartFieldsByType(
      this.seriesOptions.filter((option) => option !== DISABLED_SEPARATE_SERIES_OPTION)
    )
  }

  private refreshFieldOptions(): void {
    if (!this.form?.controls.datasource.value) return

    this.resetDatasourceSpecificOptions(
      ChartDataSource[this.form.controls.datasource.value as keyof typeof ChartDataSource]
    )
    this.updateExcludeInactiveVisibility()
  }

  //#region  ON FORM CHANGES

  private subscribeToFormChanges(form: UntypedFormGroup) {
    form.get('cardType')?.valueChanges.subscribe((cardType: CardType) => {
      this.cardTypeChanged(form, cardType)
    })

    form.get('chartType')?.valueChanges.subscribe((chartType: string) => {
      //this.clearTargetOverridesOnSignificantChange()
      const valueAgg = this.form?.controls.valueAggregation.value
      if (
        valueAgg &&
        valueAgg.toLowerCase().includes('average') &&
        chartType === ChartType[ChartType['Percentage Bar']]
      ) {
        this.notiService.showInfo(
          'You cannot use percentage bar charts with average or rolling average value aggregation'
        )
        this.form?.controls.valueAggregation.setValue(null)
      }
    })

    form
      .get('datasource')
      ?.valueChanges.pipe(filter((source) => source!))
      .subscribe((source: string) => {
        this.clearTargetOverridesOnSignificantChange()
        this.datasourceChanged(form, source)
        this.updateFormValidity()
      })

    form.get('taskType2')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      this.updateTaskDateFields()
      this.form?.controls.task1DateField.updateValueAndValidity()
      this.form?.controls.task2DateField.updateValueAndValidity()
    })

    form.get('taskMode')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      this.form?.controls.taskType.updateValueAndValidity()
      this.form?.controls.taskCode.updateValueAndValidity()
    })

    form.get('rangeType')?.valueChanges.subscribe((rangeType: string) => {
      this.clearTargetOverridesOnSignificantChange()
      this.updateDateFilterFieldVisibility()
      this.form?.controls.dateFilterField.updateValueAndValidity()
      const start = this.form?.get('start')
      const end = this.form?.get('end')
      if (rangeType == DateRangeType[DateRangeType.Custom]) {
        start?.setValue(this.card?.dateFrom)
        end?.setValue(this.card?.dateTo)
      }
      if (DateUtilsService.isPresetDateRangeStr(rangeType)) {
        this.isPresetDateRange = true
        this.recalculatePresetStartEnd()
      } else {
        this.isPresetDateRange = false
      }
    })

    form.get('includeCurrentPartialPeriod')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      const rangeType = this.form?.controls.rangeType.value
      if (DateUtilsService.isPresetDateRangeStr(rangeType)) {
        this.recalculatePresetStartEnd()
      }
    })

    form.get('includeRecordsWithNoDate')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('grouping')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      const rangeType = this.form?.controls.rangeType.value
      if (this.rangeInvalidForGrouping(rangeType)) {
        this.form?.controls.rangeType.setValue(undefined)
      } else {
        if (DateUtilsService.isPresetDateRangeStr(rangeType)) {
          this.recalculatePresetStartEnd()
        }
      }
    })

    form.get('start')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      this.checkGroupingAlignsToCustomRange()
    })

    form.get('end')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
      this.checkGroupingAlignsToCustomRange()
    })

    form.get('argumentField')?.valueChanges.subscribe((argField) => {
      this.clearTargetOverridesOnSignificantChange()
      this.argFieldIsDateType =
        this.argFieldOptions.filter((af) => af.dataField === argField)[0]?.dataType === 'date'

      this.updateExcludeInactiveVisibility()

      if (argField && this.usingRollingPeriods && !this.argFieldIsDateType) {
        this.notiService.showInfo(
          'Non time/date based argument groupings cannot be used with rolling periods'
        )
        this.form?.controls.argumentField.setValue(null)
      }

      const rangeType = this.form?.controls.rangeType.value
      if (DateUtilsService.isPresetDateRangeStr(rangeType)) {
        this.recalculatePresetStartEnd()
      }

      this.checkGroupingAlignsToCustomRange()
    })

    form.get('valueField')?.valueChanges.subscribe((valueField) => {
      this.clearTargetOverridesOnSignificantChange()
      this.showValueAggregation = this.valueFieldRequiresAggregation(valueField)
      this.updateShowTrackingField(valueField)
      if (this.isMetric && form.controls.valueField.dirty) {
        this.applyTargetComparisonDefault(valueField)
      }
    })

    form.get('valueAggregation')?.valueChanges.subscribe((valueAgg) => {
      this.clearTargetOverridesOnSignificantChange()
      this.usingRollingPeriods = valueAgg?.toLowerCase().includes('rolling')
      if (this.usingRollingPeriods && !this.argFieldIsDateType) {
        this.notiService.showInfo(
          'Rolling periods can only be used with time/date based argument grouping'
        )
        this.form?.controls.valueAggregation.setValue(null)
        valueAgg = null
      }

      const chartType = this.form?.controls.chartType.value
      if (
        valueAgg &&
        valueAgg.toLowerCase().includes('average') &&
        chartType === ChartType[ChartType['Percentage Bar']]
      ) {
        this.notiService.showInfo('You cannot use averages with percentage bar charts')
        this.form?.controls.valueAggregation.setValue(null)
        valueAgg = null
      }

      this.cumulativeAggregation =
        valueAgg === ValueAggregationType[ValueAggregationType.Cumulative]
    })

    form.get('taskType')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('taskCode')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('task1DateField')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('task2DateField')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('taskDurationOption')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('showFilteredCategoriesOnly')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('dateFilterField')?.valueChanges.subscribe(() => {
      this.clearTargetOverridesOnSignificantChange()
    })

    form.get('splitByField')?.valueChanges.subscribe((splitBy) => {
      this.clearTargetOverridesOnSignificantChange()
      this.updateExcludeInactiveVisibility()
      this.updateSplitByVisibility(splitBy)
    })

    form.get('metricDisplayType')?.valueChanges.subscribe(() => {
      this.updateMetricSplitVisibility()
    })

    form.get('targetValue')?.valueChanges.subscribe((targetValue) => {
      if (targetValue == null && this.metricSubsetModeRequiresTarget()) {
        form.controls.metricSplitSubsetMode.setValue(MetricSplitSubsetMode.Highest)
      }
    })
  }

  private updateMetricSplitVisibility(): void {
    this.showGroupBySeries = !this.isMetric || this.canSplitMetric
    this.updateShowFilteredCategoriesVisibility()
    if (!this.showGroupBySeries) {
      this.form?.controls.splitByField.setValue(DISABLED_SEPARATE_SERIES_OPTION)
    }
  }

  private updateShowFilteredCategoriesVisibility(): void {
    const datasource = ChartDataSource[
      this.form?.controls.datasource.value as keyof typeof ChartDataSource
    ]
    this.showShowFilteredCategoriesOnly =
      datasource !== ChartDataSource.TaskDurations && (!this.isMetric || this.canSplitMetric)
  }

  private updateSplitByVisibility(splitBy: ChartField): void {
    if (this.isMetric || splitBy.dataField === DISABLED_SEPARATE_SERIES) {
      this.hideStartingValue = false
      this.hideChartColorSelection = false
    } else {
      this.hideStartingValue = true
      this.hideChartColorSelection = true
    }
  }

  rangeInvalidForGrouping(range: string): boolean {
    if (!this.argFieldIsDateType) {
      return false
    }
    return !this.dateUtils.isRangeCompatibleWithGrouping(this.form?.controls.grouping.value, range)
  }

  private recalculatePresetStartEnd() {
    const [start, end] = DateUtilsService.getGroupingStartAndEndDate(
      DateRangeType[this.form?.controls.rangeType.value as keyof typeof DateRangeType],
      this.form?.controls.includeCurrentPartialPeriod.value,
      DateAxisType[this.form?.controls.grouping.value as keyof typeof DateAxisType],
      this.argFieldIsDateType
    )
    this.form?.controls.start.setValue(start.toDate())
    this.form?.controls.end.setValue(end.toDate())
  }

  private cardTypeChanged(form: UntypedFormGroup, cardType: CardType): void {
    const nextIsMetric = cardType === CardType.Metric
    if (nextIsMetric === this.isMetric) return

    if ((!nextIsMetric && this.isExtraSmallCard) || (nextIsMetric && this.metricTypeDisabled)) {
      form.controls.cardType.setValue(this.isMetric ? CardType.Metric : CardType.Chart, {
        emitEvent: false,
      })
      return
    }

    this.isMetric = nextIsMetric
    this.updateMetricSplitVisibility()
    form.controls.chartType.setValidators(this.isMetric ? [] : [Validators.required])
    form.controls.argumentField.setValidators(this.isMetric ? [] : [Validators.required])
    this.updateSplitByVisibility(form.controls.splitByField.value)

    const source = form.controls.datasource.value
    if (source) this.datasourceChanged(form, source, false)
    this.updateExcludeInactiveVisibility()

    if (this.isMetric && this.valueFieldRequiresAggregation(form.controls.valueField.value)) {
      const aggregation = form.controls.valueAggregation.value as string | undefined
      const supportedAggregations = this.aggregationOptions
      if (aggregation && !supportedAggregations.includes(aggregation)) {
        form.controls.valueAggregation.setValue(
          aggregation.includes('Average')
            ? ValueAggregationType[ValueAggregationType.Average]
            : ValueAggregationType[ValueAggregationType.Sum]
        )
      }
    }

    this.updateFormValidity()
  }

  private datasourceChanged(form: UntypedFormGroup, source: string, resetFormValues = true) {
    const src = ChartDataSource[source as keyof typeof ChartDataSource]
    if (src == null) {
      this.logger.log(
        'edit-card',
        'adjustFormOnDatasourceSelection',
        "couldn't interpret source string"
      )
      throw Error("couldn't interpret source string in adjustFormOnDatasourceSelection")
    }

    const datasource = ChartDataSource[source as keyof typeof ChartDataSource]

    this.isTaskSource = this.datasourceConfigService.datasourceUsesTaskSelection(datasource)
    this.showTaskSelectionMode = datasource === ChartDataSource.Tasks
    this.showGroupBySeries = !this.isMetric || this.canSplitMetric

    this.resetDatasourceSpecificOptions(src)
    if (resetFormValues) this.resetDatasourceSpecificFormValues(form)

    this.showTask2Fields =
      datasource === ChartDataSource.ForecastedTasksInProgress ||
      datasource === ChartDataSource.TaskDurations ||
      datasource === ChartDataSource.JobsBetweenTasks
    this.updateTaskDateFields()
    this.showDurationAggregation = datasource === ChartDataSource.TaskDurations
    this.updateShowFilteredCategoriesVisibility()
    this.updateDateFilterFieldVisibility()

    // Tracking field selection for Forecast Tasks - fetch fields so they're ready
    const isForecastSource = datasource === ChartDataSource.ForecastedTasks
      || datasource === ChartDataSource.ForecastedTasksInProgress
    if (isForecastSource) {
      if (!this.trackingFieldOptions.length) this.fetchTrackingFields()
    } else {
      this.trackingFieldOptions = []
      form.controls.trackingField?.setValue(null)
      this.showTrackingField = false
    }

    // set defaults for datasource if no values
    if (form.controls.valueAggregation.value == null)
      form.controls.valueAggregation.setValue(
        ValueAggregationType[this.datasourceConfigService.getDefaultValueAggregation(src)]
      )
    if (!this.isMetric && form.controls.grouping.value == null)
      form.controls.grouping.setValue(
        DateAxisType[this.datasourceConfigService.getDefaultDateGrouping(src)]
      )
    if (!this.isMetric && form.controls.chartType.value == null)
      form.controls.chartType.setValue(
        ChartType[this.datasourceConfigService.getDefaultChartType(src)]
      )
    const defaultDateFilter = this.dateFilterFieldOptions.filter((df) => df.isDefault)
    if (form.controls.dateFilterField.value == null && defaultDateFilter.length) {
      form.controls.dateFilterField.setValue(defaultDateFilter[0].dataField)
    }
    const defaultValue = this.valueFieldOptions.filter((df) => df.isDefault)
    if (form.controls.valueField.value == null && defaultValue.length) {
      form.controls.valueField.setValue(defaultValue[0].dataField)
    }
    const defaultArg = this.argFieldOptions.filter((df) => df.isDefault)
    if (!this.isMetric && form.controls.argumentField.value == null && defaultArg.length) {
      form.controls.argumentField.setValue(defaultArg[0].dataField)
    }
    this.argFieldIsDateType =
      !this.isMetric &&
      this.argFieldOptions.some(
        (field) =>
          field.dataField === form.controls.argumentField.value && field.dataType === 'date'
      )

    // if there is only one option, set and hide
    if (this.valueFieldOptions.length === 1) {
      if (form.controls.valueField.value !== this.valueFieldOptions[0].dataField) {
        form.controls.valueField.setValue(this.valueFieldOptions[0].dataField)
      }
      this.showValueField = false
    } else {
      this.showValueField = true
    }
    if (this.isMetric) {
      this.showArgumentFIeld = false
      if (form.controls.datasource.dirty) {
        this.applyTargetComparisonDefault(form.controls.valueField.value)
      }
    } else if (this.argFieldOptions.length === 1) {
      if (form.controls.argumentField.value !== this.argFieldOptions[0].dataField) {
        form.controls.argumentField.setValue(this.argFieldOptions[0].dataField)
      }
      this.showArgumentFIeld = false
    } else {
      this.showArgumentFIeld = true
    }

    if (!this.showTaskSelectionMode) {
      if (form.controls.taskMode.value !== 'taskType') {
        form.controls.taskMode.setValue('taskType', { emitEvent: false })
      }
      if (form.controls.taskCode.value !== null) {
        form.controls.taskCode.setValue(null, { emitEvent: false })
      }
    }
  }

  private resetDatasourceSpecificOptions(src: ChartDataSource) {
    if (this.showGroupBySeries) this.getSeriesOptions(src)

    let filterFields = this.datasourceConfigService.getDatasourceFilterFields(src)
    this.argFieldOptions = this.datasourceConfigService.getDatasourceArgumentFields(src)
    this.valueFieldOptions = this.datasourceConfigService.getDatasourceValueFields(src)
    this.dateFilterFieldOptions = this.datasourceConfigService.getDatasourceDateFilterFields(src)
    this.rangeOptions = this.datasourceConfigService.getDateRangeOptions(src)

    this.unavailableFieldLabels = new Map()
    if (this.card?.dataSourceId === src && !this.form?.controls.datasource.dirty) {
      const addIfMissing = (options: ChartField[], dataField?: string | null): ChartField[] => {
        if (!dataField || options.some((option) => option.dataField === dataField)) return options
        const unavailable = this.datasourceConfigService.getUnavailableField(dataField)
        this.unavailableFieldLabels.set(dataField, unavailable.displayName)
        return [...options, unavailable]
      }

      if (!this.isMetric) this.argFieldOptions = addIfMissing(this.argFieldOptions, this.card.argumentField)
      if (this.showGroupBySeries && this.card.splitByField !== DISABLED_SEPARATE_SERIES) {
        this.seriesOptions = addIfMissing(this.seriesOptions, this.card.splitByField)
        this.groupSeriesOptions()
      }
      getFilterFieldNames(this.filter).forEach((dataField) => {
        filterFields = addIfMissing(filterFields, dataField)
      })
    }

    this.argFieldOptionGroups = groupChartFieldsByType(this.argFieldOptions)
    this.dataSourceFilterFields = filterFields.map((field) => ({ ...field, caption: field.displayName }))
  }

  private resetDatasourceSpecificFormValues(form: UntypedFormGroup) {
    if (form.controls.datasource.dirty) {
      this.form?.controls.taskMode.setValue('taskType')
      this.form?.controls.taskType.setValue(undefined)
      this.form?.controls.taskCode.setValue(null)
      this.form?.controls.task1DateField.setValue(undefined)
      this.form?.controls.taskType2.setValue(undefined)
      this.form?.controls.task2DateField.setValue(undefined)
      this.form?.controls.valueField.setValue(undefined)
      this.form?.controls.argumentField.setValue(undefined)
      this.form?.controls.splitByField.setValue(DISABLED_SEPARATE_SERIES_OPTION)
      this.form?.controls.dateFilterField.setValue(undefined)
      this.form?.controls.rangeType.setValue(undefined)
      this.filter = undefined
    }
  }

  private updateTaskDateFields(): void {
    if (!this.form) return

    const datasource = ChartDataSource[
      this.form.controls.datasource.value as keyof typeof ChartDataSource
    ]
    const isTaskDuration = datasource === ChartDataSource.TaskDurations
    const isPairedForecast =
      datasource === ChartDataSource.ForecastedTasksInProgress &&
      this.form.controls.taskType2.value != null

    this.showTask1DateField = isTaskDuration || isPairedForecast
    if (!this.showTask1DateField) return

    const startDateField = isTaskDuration ? 'startDate' : 'forecastStartDate'
    if (this.form.controls.task1DateField.value == null) {
      this.form.controls.task1DateField.setValue(startDateField)
    }
    if (this.form.controls.task2DateField.value == null) {
      this.form.controls.task2DateField.setValue(startDateField)
    }
  }

  private updateFormValidity() {
    this.form?.controls.task1DateField.updateValueAndValidity()
    this.form?.controls.task2DateField.updateValueAndValidity()
    this.form?.controls.taskType.updateValueAndValidity()
    this.form?.controls.taskCode.updateValueAndValidity()
    this.form?.controls.taskType2.updateValueAndValidity()
    this.form?.controls.argumentField.updateValueAndValidity()
    this.form?.controls.chartType.updateValueAndValidity()
    this.form?.controls.grouping.updateValueAndValidity()
    this.form?.controls.valueField.updateValueAndValidity()
    this.form?.controls.dateFilterField.updateValueAndValidity()
    this.form?.controls.start.updateValueAndValidity()
    this.form?.controls.end.updateValueAndValidity()
    this.form?.updateValueAndValidity()
  }

  private updateDateFilterFieldVisibility() {
    const datasource = ChartDataSource[
      this.form?.controls.datasource.value as keyof typeof ChartDataSource
    ]
    this.showDateFilterField =
      datasource !== ChartDataSource.ForecastedTasksInProgress &&
      this.form?.controls.rangeType.value !== DateRangeType[DateRangeType['All Time']]
  }

  private checkGroupingAlignsToCustomRange() {
    if (
      this.form?.controls.rangeType.value === DateRangeType[DateRangeType.Custom] &&
      this.form?.controls.start.value &&
      this.form?.controls.end.value &&
      this.argFieldIsDateType
    ) {
      this.rangeNotAlignToGrouping = !this.dateUtils.rangeAlignsToGrouping(
        DateAxisType[this.form?.controls.grouping.value as keyof typeof DateAxisType],
        this.form?.controls.start.value,
        this.form?.controls.end.value
      )
    } else {
      this.rangeNotAlignToGrouping = false
    }
    this.form?.updateValueAndValidity()
  }

  private updateExcludeInactiveVisibility() {
    if (this.isMetric) {
      this.showExcludeInactive = false
      return
    }

    const argFieldValue = this.form?.controls.argumentField.value
    const splitByField = this.form?.controls.splitByField.value as ChartField

    const argIsPersonField =
      this.argFieldOptions.find(f => f.dataField === argFieldValue)?.isPersonField === true
    const splitIsPersonField = splitByField?.isPersonField === true

    this.showExcludeInactive = argIsPersonField || splitIsPersonField
  }
  //#endregion  ON FORM CHANGES

  //#region  CUSTOM TARGETS

  toggleCustomiseTarget(): void {
    this.showCustomTargets = !this.showCustomTargets
    if (this.showCustomTargets) {
      this.generateTargetPeriods()
    }
  }

  canCustomiseTarget(): boolean {
    // Can only customise targets if:
    // 1. The card has a valid saved configuration (provideData requires a complete ChartConfig)
    // 2. There are no unsaved significant changes (changes that would affect the groupings)
    return !this.isDraft && this.cardHasValidChartConfig() && !this.hasUnsavedSignificantChanges
  }

  private cardHasValidChartConfig(): boolean {
    if (!this.card) return false

    if (this.isMetric) {
      return (
        this.card.dataSourceId != null &&
        this.card.metricDisplayType === MetricDisplayType.Gauge &&
        !!this.card.splitByField &&
        this.card.splitByField !== DISABLED_SEPARATE_SERIES
      )
    }

    return (
      this.card.chartTypeId != null &&
      this.card.dataSourceId != null &&
      this.card.argumentField != null
    )
  }

  onPeriodTargetChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement
    const value = input.value ? parseFloat(input.value) : null
    this.targetPeriods[index].value = value
  }

  clearAllTargetOverrides(): void {
    this.targetPeriods = []
    this.hasUnsavedSignificantChanges = true
  }

  private generateTargetPeriods(): void {
    if (!this.form || !this.card) {
      this.targetPeriods = []
      return
    }

    this.loadingTargetPeriods = true
    if (this.isMetric) {
      const metricConfig = this.dashboardService.getMetricConfig(this.card)
      this.chartDataService.provideMetricData(metricConfig).subscribe({
        next: (result) => {
          this.targetPeriods = (result.splitValues ?? []).map((item) => ({
            label: item.label,
            key: item.label,
            value: null,
          }))
          this.loadTargetOverridesIntoPeriods()
          this.loadingTargetPeriods = false
        },
        error: (err) => this.handleTargetPeriodError(err),
      })
      return
    }

    // Use chart data to get the actual grouping values - works for both date and non-date groupings
    const chartConfig = this.dashboardService.getChartConfig(this.card.id)
    this.chartDataService.provideData(chartConfig).subscribe({
      next: (result) => {
        // Extract groupField values from the data, preserving order
        const seenKeys = new Set<string>()
        this.targetPeriods = []

        for (const item of result.data) {
          const groupField = item.groupField
          if (groupField == null) continue

          // Generate key based on groupField type (date or category string)
          const key = String(groupField)

          if (!seenKeys.has(key)) {
            seenKeys.add(key)
            // Generate label based on type
            const label =
              groupField instanceof Date
                ? DateUtilsService.formatDateLabel(groupField, chartConfig.dateAxisTypeId)
                : String(groupField)

            this.targetPeriods.push({ label, key, value: null })
          }
        }

        this.loadTargetOverridesIntoPeriods()
        this.loadingTargetPeriods = false
      },
      error: (err) => this.handleTargetPeriodError(err),
    })
  }

  private handleTargetPeriodError(err: unknown): void {
    this.logger.log('edit-card', 'generateTargetPeriods', err)
    this.notiService.showError('Could not load grouping values for target customisation')
    this.loadingTargetPeriods = false
  }

  onFilterChanged(): void {
    this.clearTargetOverridesOnSignificantChange(true)
  }

  private clearTargetOverridesOnSignificantChange(bypassFormCheck = false): void {
    if (!bypassFormCheck && !this.form?.dirty) return

    this.hasUnsavedSignificantChanges = true
    this.showCustomTargets = false
    this.targetPeriods = []
  }

  private buildAdditionalConfig(): string {
    const config = this.card?.config ?? {}

    config.cardType = this.isMetric ? CardType.Metric : CardType.Chart

    // Only modify targetOverrides if the user interacted with the custom targets section
    // or if a significant change was made that would invalidate existing overrides
    if (this.targetPeriods.length > 0 || this.hasUnsavedSignificantChanges) {
      // Build targetOverrides from targetPeriods (only include non-null values)
      const targetOverrides: { [key: string]: number } = {}
      for (const period of this.targetPeriods) {
        if (period.value != null) {
          targetOverrides[period.key] = period.value
        }
      }

      // Only set targetOverrides if there are any
      if (Object.keys(targetOverrides).length > 0) {
        config.targetOverrides = targetOverrides
      } else {
        delete config.targetOverrides
      }
    }
    // If targetPeriods is empty and no significant changes were made,
    // preserve the existing targetOverrides in config

    // Tracking field for Forecast Tasks
    const trackingFieldValue = this.form?.controls.trackingField?.value
    if (trackingFieldValue) {
      config.trackingField = trackingFieldValue
    } else {
      delete config.trackingField
    }

    // Metric description
    const showDescription = this.form?.controls.showDescription?.value
    const description = (this.form?.controls.description?.value ?? '').trim()
    if (showDescription) {
      config.showDescription = true
      if (description) {
        config.description = description
      } else {
        delete config.description
      }
    } else {
      delete config.showDescription
      delete config.description
    }

    // Exclude inactive users
    const excludeInactive = this.form?.controls.excludeInactiveUsers?.value
    if (excludeInactive) {
      config.excludeInactiveUsers = true
    } else {
      delete config.excludeInactiveUsers
    }

    const includeRecordsWithNoDate = this.form?.controls.includeRecordsWithNoDate?.value
    if (includeRecordsWithNoDate) {
      config.includeRecordsWithNoDate = true
    } else {
      delete config.includeRecordsWithNoDate
    }

    // Metric display type (numeric vs gauge)
    if (this.isMetric) {
      const metricDisplayTypeValue = this.form?.controls.metricDisplayType?.value as MetricDisplayType
      if (metricDisplayTypeValue && metricDisplayTypeValue !== MetricDisplayType.Numeric) {
        config.metricDisplayType = metricDisplayTypeValue
      } else {
        delete config.metricDisplayType
      }

      config.targetComparison = this.form?.controls.targetComparison?.value as TargetComparison

      const subsetMode = this.form?.controls.metricSplitSubsetMode?.value as MetricSplitSubsetMode
      if (subsetMode && subsetMode !== MetricSplitSubsetMode.Highest) {
        config.metricSplitSubsetMode = subsetMode
      } else {
        delete config.metricSplitSubsetMode
      }

      const displayCount = Number(this.form?.controls.metricSplitDisplayCount?.value)
      if (displayCount >= 1 && displayCount < 8) {
        config.metricSplitDisplayCount = displayCount
      } else {
        delete config.metricSplitDisplayCount
      }
    }

    if (this.usesTaskCodeSelection()) {
      const taskCode = this.form?.controls.taskCode?.value
      if (taskCode) {
        config.taskCode = taskCode
      } else {
        delete config.taskCode
      }
    } else {
      delete config.taskCode
    }

    return JSON.stringify(config)
  }

  private getInitialTargetComparison(): TargetComparison {
    if (this.card?.config?.targetComparison) {
      return this.card.config.targetComparison
    }

    if (this.card?.dataSourceId == null) {
      return TargetComparison.AtLeast
    }

    const valueField = this.card.config?.trackingField && this.card.valueField === DEFAULT_VALUE_FIELD
      ? TRACKING_VALUE_FIELD_SENTINEL
      : this.card.valueField

    return this.datasourceConfigService
      .getDatasourceValueFields(this.card.dataSourceId)
      .find((field) => field.dataField === valueField)?.defaultTargetComparison
      ?? TargetComparison.AtLeast
  }

  private metricSubsetModeRequiresTarget(): boolean {
    const mode = this.form?.controls.metricSplitSubsetMode?.value as MetricSplitSubsetMode
    return mode !== MetricSplitSubsetMode.Highest && mode !== MetricSplitSubsetMode.Lowest
  }

  private applyTargetComparisonDefault(valueField: string | undefined): void {
    const comparison = this.valueFieldOptions
      .find((field) => field.dataField === valueField)?.defaultTargetComparison
      ?? TargetComparison.AtLeast

    this.form?.controls.targetComparison.setValue(comparison)
  }

  private getInitialTaskSelectionMode(): TaskSelectionMode {
    if (this.card?.taskMasterId != null) {
      return 'taskType'
    }

    if (this.card?.config?.taskCode) {
      return 'taskCode'
    }

    return 'taskType'
  }

  private usesTaskCodeSelection(): boolean {
    return (
      this.form?.controls.datasource.value === ChartDataSource[ChartDataSource.Tasks] &&
      this.form?.controls.taskMode.value === 'taskCode'
    )
  }

  private loadTargetOverridesIntoPeriods(): void {
    const overrides = this.card?.config?.targetOverrides
    if (!overrides) return

    for (const period of this.targetPeriods) {
      if (overrides[period.key] != null) {
        period.value = overrides[period.key]
      }
    }
  }
  //#endregion  CUSTOM TARGETS

  //#region  FORM FIELD VALIDATORS

  private rollingPeriodAndRangeValidator(): ValidatorFn {
    return (_control: AbstractControl): ValidationErrors | null => {
      if (this.usingRollingPeriods && this.rangeNotAlignToGrouping) {
        return { rollingPeriodRangeError: true }
      }
      return null
    }
  }

  private requiredIfCustomRange(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.form?.controls.rangeType.value == DateRangeType[DateRangeType.Custom]) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private requiredForChart(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.isMetric) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private requiredIfCustomValueField(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.valueFieldRequiresAggregation(this.form?.controls.valueField.value)) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private valueFieldRequiresAggregation(valueField?: string): boolean {
    return valueField != null && valueField != DEFAULT_VALUE_FIELD
  }

  private updateShowTrackingField(valueField?: string) {
    const isTrackingSelected = valueField === TRACKING_VALUE_FIELD_SENTINEL
    this.showTrackingField = isTrackingSelected
    if (!isTrackingSelected) {
      this.form?.controls.trackingField?.setValue(null)
    } else if (!this.trackingFieldOptions.length) {
      this.fetchTrackingFields()
    }
  }

  private requiredIfTaskDurationDatasource(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.form?.controls.datasource.value === ChartDataSource[ChartDataSource.TaskDurations]) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private requiredIfSecondTaskDatasource(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const datasource = this.form?.controls.datasource.value
      if (
        datasource === ChartDataSource[ChartDataSource.TaskDurations] ||
        datasource === ChartDataSource[ChartDataSource.JobsBetweenTasks]
      ) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private taskTypeRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isTaskSource && !this.usesTaskCodeSelection()) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }

  private dateFilterFieldRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.form?.controls.datasource.value !==
          ChartDataSource[ChartDataSource.ForecastedTasksInProgress] &&
        this.form?.controls.rangeType.value !== DateRangeType[DateRangeType['All Time']]
      ) {
        return Validators.required(control)
      }

      return null
    }
  }

  private taskCodeRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.usesTaskCodeSelection()) {
        return Validators.required(control)
      }

      return null
    }
  }

  private taskDateFieldRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const datasource = this.form?.controls.datasource.value
      if (
        datasource === ChartDataSource[ChartDataSource.TaskDurations] ||
        (datasource === ChartDataSource[ChartDataSource.ForecastedTasksInProgress] &&
          this.form?.controls.taskType2.value != null)
      ) {
        return Validators.required(control)
      } else {
        return null
      }
    }
  }
  //#endregion  FORM FIELD VALIDATORS
}
